export type Environment = {
  name: string;
  baseUrl: string;
  restApiUrl: string;
  cmsApiUrl: string;
  cmsApiKey: string;
  checkoutUrl: string;
  institutional: string;
  mode?: string;
  searchApiUrl: string;
};

export enum EnvironmentNameEnum {
  DEV = 'dev',
  LOCAL = 'local',
  TEST = 'test',
  HOMOLOG = 'homolog',
  PRODUCTION = 'production',
}

type EnvironmentsMap = Record<EnvironmentNameEnum, Environment>;

const dev: Environment = {
  name: EnvironmentNameEnum.DEV,
  checkoutUrl: 'https://checkout-test.medprev.app',
  baseUrl: 'http://localhost:8080',
  restApiUrl: 'http://localhost:3000',
  cmsApiUrl: 'https://cms-test.medprev.app',
  cmsApiKey: process.env.MEDPREV_CMS_API_KEY ?? '',
  institutional: 'https://institucional-test.medprev.online',
  searchApiUrl: 'https://search-api-test.medprev.app',
};

const local: Environment = {
  name: EnvironmentNameEnum.LOCAL,
  checkoutUrl: 'https://checkout-test.medprev.app',
  baseUrl: 'http://localhost:8080',
  restApiUrl: process.env.NEXT_PUBLIC_MEDPREV_REST_API_BASE_URL || 'http://localhost:3000',
  cmsApiKey: process.env.MEDPREV_CMS_API_KEY ?? '',
  cmsApiUrl: 'https://cms-test.medprev.app',
  institutional: 'https://institucional-test.medprev.online',
  searchApiUrl: process.env.NEXT_PUBLIC_SEARCH_API_URL || 'http://localhost:3001',
};

const test: Environment = {
  name: EnvironmentNameEnum.TEST,
  checkoutUrl: 'https://checkout-test.medprev.app',
  baseUrl: 'https://alpha.medprev.online',
  restApiUrl: process.env.MEDPREV_REST_API_URL || 'https://api-test.medprev.app',
  cmsApiUrl: 'https://cms-test.medprev.app',
  cmsApiKey: process.env.MEDPREV_CMS_API_KEY ?? '',
  institutional: 'https://institucional-test.medprev.online',
  searchApiUrl: 'https://search-api-test.medprev.app',
};

const homolog: Environment = {
  name: EnvironmentNameEnum.HOMOLOG,
  checkoutUrl: 'https://checkout-homolog.medprev.app',
  baseUrl: 'https://beta.medprev.online',
  restApiUrl: process.env.MEDPREV_REST_API_URL || 'https://api-hml.medprev.app',
  cmsApiUrl: 'https://cms-hml.medprev.app',
  cmsApiKey: process.env.MEDPREV_CMS_API_KEY ?? '',
  institutional: 'https://institucional-homolog.medprev.online',
  searchApiUrl: 'https://search-api-hml.medprev.app',
};

const production: Environment = {
  name: EnvironmentNameEnum.PRODUCTION,
  checkoutUrl: 'https://checkout.medprev.online',
  baseUrl: 'https://medprev.online',
  restApiUrl: process.env.MEDPREV_REST_API_URL || 'https://api.medprev.app',
  cmsApiUrl: 'https://cms.medprev.app',
  cmsApiKey: process.env.MEDPREV_CMS_API_KEY ?? '',
  institutional: 'https://medprev.online',
  searchApiUrl: 'https://search-api.medprev.app',
};

const environmentsMap: EnvironmentsMap = {
  dev,
  local,
  test,
  homolog,
  production,
};

function getEnvironmentNameFromEnv(): EnvironmentNameEnum {
  if (process.env.ENVIRONMENT_NAME) {
    return process.env.ENVIRONMENT_NAME as EnvironmentNameEnum;
  }

  return process.env.NODE_ENV === 'development'
    ? EnvironmentNameEnum.LOCAL
    : EnvironmentNameEnum.PRODUCTION;
}

export function getEnvironmentNameFromUrl(): EnvironmentNameEnum {
  const url = new URL(window.location.href);

  if (url.hostname.includes('localhost')) {
    return EnvironmentNameEnum.LOCAL;
  }

  if (url.hostname.includes('alpha')) {
    return EnvironmentNameEnum.TEST;
  }

  if (url.hostname.includes('beta')) {
    return EnvironmentNameEnum.HOMOLOG;
  }

  return EnvironmentNameEnum.PRODUCTION;
}

export const getEnvironmentName = (): EnvironmentNameEnum => {
  return typeof window === 'undefined'
    ? getEnvironmentNameFromEnv()
    : getEnvironmentNameFromUrl();
};

export function getEnvironment(): Environment {
  return environmentsMap[getEnvironmentName()]
    || environmentsMap[EnvironmentNameEnum.PRODUCTION];
}
